/**
 * Der Verlauf eines Datenpunkts (HM-053).
 *
 * Der Aufbau folgt `PointHistory` aus `ui_kits/dashboard/history.jsx` und damit
 * der Reihenfolge der Fragen: Was ist das für ein Wert, wie sieht er über die
 * Zeit aus, was lässt sich über den Zeitraum sagen, und was stand zuletzt da.
 *
 * **Erreicht wird die Seite über die Zeile in der Übersicht des Geräts.** Sie ist
 * eine Ebene tiefer im selben Bereich; die Seitenleiste zeigt weiter
 * „Geräteverwaltung", und der Weg zurück heißt nach seinem Ziel.
 *
 * **Was der Entwurf zeigt und es hier nicht gibt — oder anders:**
 *
 * | Im Kit                                | Hier                                            |
 * | ------------------------------------- | ----------------------------------------------- |
 * | Beispielkurve aus einem Zufallsgenerator | echte Rohwerte, auf ein Raster verdichtet    |
 * | Linie durchgezogen über jede Lücke    | die Linie **bricht ab** (Regel 8)               |
 * | „Mittel je Tag" bei Zählerständen     | fehlt — eine gerechnete Zahl ohne festen Bezug  |
 * | Zuwachs immer                         | **nicht** über einen Rücksprung hinweg (Regel 4)|
 * | „Wertwechsel" für Textdatenpunkte     | der Katalogeintrag selbst — es gibt keine Reihe |
 * | Kurve ohne Spanne                     | Band aus `min`/`max` bei gemittelten Werten     |
 *
 * **Der Zeitraum steht in der Adresse** (`?zeitraum=`), nicht in einem
 * `useState`: Wer jemandem „die Spitze von letzter Woche" zeigen will, schickt
 * einen Link, und ein Neuladen spränge sonst auf 24 Stunden zurück.
 *
 * **Die Fenster sind rollierend und heißen so.** „24 Stunden" ist nicht
 * „heute" — ein Kalendertag verlangt die Zeitzone der Anlage und eine
 * Tagesgrenze, die zweimal im Jahr 23 oder 25 Stunden lang ist. Tagesvergleiche
 * kommen mit den Zählwerksreihen (HM-008, HM-010).
 */
import { useMemo } from "react";
import { Alert, Button, Card, EmptyState, SegmentedControl, icons } from "@hausmeter/ui";
import { formatInstant } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";
import { formatTimeAgo } from "../lib/format.js";
import { datapointFormatter, formatDatapointValue } from "../lib/datapoint-format.js";
import { RANGE_LABELS, rangeWindow, retentionCutsInto } from "../lib/history-range.js";
import { navigate, HISTORY_RANGES, type HistoryRange, type Route } from "../lib/router.js";
import { useDatapoints, useDatapointHistory, useDevice } from "../lib/queries.js";
import { HistoryChart } from "../components/history-chart.js";
import { HistoryTiles } from "../components/history-tiles.js";
import { Section } from "../components/section.js";

type Datapoint = ApiComponents["schemas"]["Datapoint"];
type History = ApiComponents["schemas"]["DatapointHistoryResponse"];

const MONO = { fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" } as const;

/** Wie viele Zeilen die Tabelle am Fuß zeigt — die jüngsten zuerst. */
const RECENT_ROWS = 8;

export function DatapointDetailScreen({
  siteId,
  siteTimeZone,
  deviceId,
  datapointId,
  range,
  onBack,
}: {
  siteId: string;
  /** Zeitpunkte stehen in der Zeitzone der Anlage, nie in der des Browsers. */
  siteTimeZone: string;
  deviceId: string;
  datapointId: string;
  range: HistoryRange;
  /** Zurück in die Übersicht des Geräts, wo die Datenpunkte stehen. */
  onBack: () => void;
}) {
  const device = useDevice(deviceId);
  const datapoints = useDatapoints(deviceId);

  const point = datapoints.data?.items.find((entry) => entry.id === datapointId) ?? null;
  const isText = point?.valueType === "string";

  /**
   * **Einmal je Zeitraum gebildet, nicht bei jedem Zeichnen.** Sonst trüge der
   * Abfrageschlüssel jedes Mal ein anderes `to`, und die Reihe würde endlos
   * nachgeladen.
   */
  const window = useMemo(() => rangeWindow(range), [range]);

  const history = useDatapointHistory(
    isText ? null : deviceId,
    isText ? null : datapointId,
    window,
  );

  const goRange = (next: HistoryRange): void =>
    navigate({ kind: "datapoint", siteId, deviceId, datapointId, range: next } as Route);

  const channelName =
    point?.channelId === null || point === null
      ? null
      : (device.data?.channels.find((channel) => channel.id === point.channelId)?.name ?? null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 1100 }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          alignSelf: "flex-start",
          padding: "5px 10px 5px 6px",
          border: "none",
          background: "transparent",
          borderRadius: "var(--radius-sm)",
          cursor: "pointer",
          fontFamily: "var(--font-body)",
          fontSize: 13,
          fontWeight: 600,
          color: "var(--text-muted)",
        }}
      >
        <icons.IconArrowLeft size={16} />
        {device.data?.name ?? "Gerät"} · Werte
      </button>

      {datapoints.isPending && <Card padding="sm">Wird geladen …</Card>}

      {/**
       * Ein Datenpunkt, den der Katalog nicht führt, ist ein **Zustand** und
       * kein Ladefehler: Die Adresse kann von gestern sein, oder das Gerät hat
       * seinen Katalog seither geändert.
       */}
      {!datapoints.isPending && point === null && (
        <Card>
          <EmptyState
            title="Diesen Wert gibt es nicht"
            icon={<icons.IconGauge size={30} />}
            size="md"
            action={
              <Button variant="secondary" onClick={onBack}>
                Zu den Werten
              </Button>
            }
          >
            Er steht nicht mehr im Katalog dieses Geräts, oder die Adresse stimmt nicht mehr.
          </EmptyState>
        </Card>
      )}

      {point !== null && (
        <>
          <Header
            point={point}
            deviceName={device.data?.name ?? null}
            channelName={channelName}
            range={range}
            onRange={goRange}
            showRanges={!isText}
          />

          {isText ? (
            <TextValueCard point={point} />
          ) : (
            <NumericHistory
              point={point}
              history={history.data}
              pending={history.isPending}
              failed={history.isError}
              range={range}
              timeZone={siteTimeZone}
            />
          )}
        </>
      )}
    </div>
  );
}

/** Name, Herkunft, Zeitraum — die Kopfzeile des Entwurfs. */
function Header({
  point,
  deviceName,
  channelName,
  range,
  onRange,
  showRanges,
}: {
  point: Datapoint;
  deviceName: string | null;
  channelName: string | null;
  range: HistoryRange;
  onRange: (range: HistoryRange) => void;
  showRanges: boolean;
}) {
  /**
   * Die Unterzeile nennt nur, was es gibt. Ein Gerät ohne Kanäle hat keinen, und
   * ein „Kanal: —" wäre eine Leerstelle, die nach einem Fehler aussieht.
   */
  const parts = [deviceName, channelName].filter((part): part is string => part !== null);

  return (
    <div
      style={{
        display: "flex",
        gap: "var(--sp-4)",
        alignItems: "flex-end",
        justifyContent: "space-between",
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <h1
          style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontSize: 21,
            fontWeight: 700,
            letterSpacing: "var(--tracking-tight)",
            color: "var(--text-strong)",
          }}
        >
          {point.name}
        </h1>
        <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
          {parts.join(" · ")}
          {parts.length > 0 && " · "}
          <span style={MONO}>{point.key}</span>
          {point.obisCode !== null && (
            <>
              {" · "}
              <span style={MONO}>{point.obisCode}</span>
            </>
          )}
        </p>
      </div>

      {showRanges && (
        <SegmentedControl
          label="Zeitraum"
          size="sm"
          value={range}
          onChange={onRange}
          options={HISTORY_RANGES.map((id) => ({ value: id, label: RANGE_LABELS[id] }))}
        />
      )}
    </div>
  );
}

/**
 * Ein **Textdatenpunkt** hat keinen Verlauf (HM-052).
 *
 * Sein Wert steht am Katalogeintrag und ändert sich über die Lebensdauer eines
 * Zählers nicht — Hersteller, Geräte-ID. Der Entwurf zeigt an dieser Stelle
 * „Wertwechsel" mit zwei Zeilen; die gibt es nicht, denn gespeichert ist nur der
 * letzte Stand. Eine erfundene Historie wäre hier besonders verführerisch und
 * besonders falsch.
 *
 * **`textValueAt` ist der Zeitpunkt der letzten Änderung, nicht der letzten
 * Meldung.** Der Katalog ist behalten und kommt bei jedem Verbindungsaufbau
 * erneut; stünde dort der Empfang, hieße es dauerhaft „gerade eben".
 */
function TextValueCard({ point }: { point: Datapoint }) {
  return (
    <Section title="Angabe des Zählers" subtitle="Kein Wert, sondern eine Stammangabe.">
      <Card>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          <span
            style={{ ...MONO, fontSize: 18, color: "var(--text-strong)", wordBreak: "break-all" }}
          >
            {point.textValue}
          </span>
          {point.textValueAt !== null && (
            <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
              Zuletzt geändert {formatTimeAgo(point.textValueAt)}
            </span>
          )}
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
            Dieser Wert trägt einen Text und keine Messreihe: Was hier steht, sagt der Zähler
            über sich selbst. Ein Verlauf daraus wäre eine Zeitreihe aus einer Konstanten.
          </p>
        </div>
      </Card>
    </Section>
  );
}

/** Diagramm, Kennzahlen und die jüngsten Werte. */
function NumericHistory({
  point,
  history,
  pending,
  failed,
  range,
  timeZone,
}: {
  point: Datapoint;
  history: History | undefined;
  pending: boolean;
  failed: boolean;
  range: HistoryRange;
  timeZone: string;
}) {
  if (pending) return <Card padding="sm">Der Verlauf wird geladen …</Card>;

  if (failed || history === undefined) {
    return (
      <Card>
        <EmptyState title="Der Verlauf ist nicht abrufbar" icon={<icons.IconGauge size={30} />}>
          Die Reihe konnte nicht geladen werden. Der Katalogeintrag steht; was fehlt, sind die Werte
          dazu.
        </EmptyState>
      </Card>
    );
  }

  const stats = history.statistics;
  /**
   * Die Größenordnung der ganzen Reihe kommt aus ihrem größten Betrag — sonst
   * stünde an der einen Achsenkante „1,1 MW" und an der nächsten „980 kW".
   */
  const formatter = datapointFormatter(point, Math.max(...spanOf(history), 0));

  const recent = [...history.points].slice(-RECENT_ROWS).reverse();
  const counter = history.aggregate === "last";

  /**
   * **Die Tabelle hat ihre eigene Größenordnung — die der acht gezeigten
   * Zeilen.** Mit der Stufe der ganzen Reihe stand dort achtmal „0,1 kW" und
   * „0,2 kW", weil irgendwo am Vormittag eine Spitze von 2,5 kW liegt: Eine
   * Kolonne, in der sich nichts mehr unterscheidet, ist keine Tabelle mehr.
   * Gemeinsam muss die Stufe trotzdem sein, sonst springt die Einheit von Zeile
   * zu Zeile.
   */
  const recentFormatter = datapointFormatter(
    point,
    Math.max(...recent.map((entry) => Math.abs(entry.v)), 0),
  );

  return (
    <>
      {/**
       * **Ein Rücksprung ist die eine Auffälligkeit, die eine Erklärung
       * braucht** (Regel 4). Ohne sie stünde bei „Zuwachs" ein Strich, und
       * niemand wüsste, warum.
       */}
      {stats?.counterReset === true && (
        <Alert tone="warning">
          Der Wert ist in diesem Zeitraum <strong>zurückgesprungen</strong>. Das geschieht, wenn das
          Gerät einen anderen Zähler liest oder zurückgesetzt wurde. Ein Zuwachs wird deshalb nicht
          gebildet — die Differenz über einen Wechsel hinweg wäre keine Verbrauchsmenge.
        </Alert>
      )}

      {retentionCutsInto(history, history.retainedFrom) && (
        <Alert tone="info">
          Einzelne Meldungen werden nur begrenzt aufbewahrt. Vor{" "}
          {formatInstant(new Date(history.retainedFrom ?? ""), timeZone, "de-DE", "date")} liegen
          deshalb keine mehr vor — dort fehlt nichts.
        </Alert>
      )}

      <Card>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: 8,
            marginBottom: "var(--sp-4)",
          }}
        >
          {stats === null ? (
            <span style={{ fontSize: 14, color: "var(--text-muted)" }}>
              In diesen {RANGE_LABELS[range]} ist kein Wert eingegangen.
            </span>
          ) : (
            <>
              {/**
               * **Der große Wert steht in seiner eigenen Größenordnung**, nicht
               * in der der Reihe. Sie gilt für Achse und Tabelle, wo Zahlen
               * untereinander vergleichbar sein müssen; hier steht eine
               * einzelne, und aus 168 W würde sonst „0,2 kW", weil die Reihe
               * eine Spitze von 2,8 kW enthält.
               */}
              <span
                style={{
                  ...MONO,
                  fontSize: 30,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: "var(--text-strong)",
                }}
              >
                {formatDatapointValue(stats.last.v, point)}
              </span>
              {/* `nowrap`: Ohne das bricht „Stand vor 2 Minuten" auf einem
                  Telefon in drei Zeilen neben der Zahl, statt unter sie zu
                  rücken. */}
              <span
                style={{ fontSize: 12.5, whiteSpace: "nowrap", color: "var(--text-faint)" }}
              >
                Stand {formatTimeAgo(stats.last.t)}
              </span>
            </>
          )}
        </div>

        <div style={{ marginBottom: -14 }}>
          <HistoryChart history={history} point={point} formatter={formatter} timeZone={timeZone} />
        </div>
      </Card>

      {stats !== null && <HistoryTiles history={history} stats={stats} point={point} />}

      {/**
       * **Ohne einen einzigen Wert steht hier nichts von Lücken.** Bei einer
       * leeren Reihe wäre der ganze Zeitraum eine Lücke — „in 288
       * Zeitabschnitten (100 % des Zeitraums)" sagt dasselbe wie der Satz in der
       * Karte darüber, nur umständlicher. Es fehlt dann nicht ein Teil der
       * Reihe, es fehlt die Reihe.
       */}
      <p
        style={{
          margin: 0,
          maxWidth: 640,
          fontSize: 12.5,
          lineHeight: 1.6,
          color: "var(--text-muted)",
        }}
      >
        {stats === null ? (
          <>
            Sobald das Gerät hierfür etwas meldet, entsteht hier ein Verlauf. Einzelne Meldungen
            werden nur begrenzt aufbewahrt.
          </>
        ) : (
          <>
            {gapNote(history)} Ein Wert je{" "}
            {history.resolution === "5m"
              ? "5 Minuten"
              : history.resolution === "15m"
                ? "15 Minuten"
                : history.resolution === "1h"
                  ? "Stunde"
                  : "Tag"}
            , gebildet aus {counter ? "dem Stand am Ende" : "dem Mittel"} der Meldungen darin
            {counter ? "" : "; die Spanne darum sind ihr kleinster und größter Wert"}.
          </>
        )}
      </p>

      {recent.length > 0 && (
        <Section
          title="Zuletzt gemeldet"
          subtitle={`Die jüngsten ${recent.length} Werte des Zeitraums.`}
        >
          <Card padding="none">
            {recent.map((entry, index) => (
              <div
                key={entry.t}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: "var(--sp-4)",
                  alignItems: "center",
                  padding: "10px var(--sp-4)",
                  borderBottom:
                    index < recent.length - 1 ? "1px solid var(--border-subtle)" : "none",
                }}
              >
                <span style={{ ...MONO, fontSize: 12.5, color: "var(--text-muted)" }}>
                  {formatInstant(new Date(entry.t), timeZone, "de-DE")}
                </span>
                <span style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                  <span
                    style={{
                      ...MONO,
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: "var(--text-strong)",
                    }}
                  >
                    {recentFormatter.value(entry.v)}
                  </span>
                  {recentFormatter.unit !== "" && (
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {recentFormatter.unit}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </Card>
        </Section>
      )}
    </>
  );
}

/**
 * Was von den Lücken zu sagen ist.
 *
 * **Eine Lücke wird benannt und nicht weggeglättet** (Regel 8). Die Linie bricht
 * ohnehin ab; der Satz sagt, dass das Absicht ist und woran es liegt.
 */
function gapNote(history: History): string {
  if (history.gaps.length === 0) return "Der Zeitraum ist lückenlos belegt.";

  const missing = history.gaps.reduce((sum, gap) => sum + gap.missingBuckets, 0);
  const share = Math.round((1 - history.coverage) * 100);

  return (
    `In ${missing === 1 ? "einem Zeitabschnitt" : `${missing} Zeitabschnitten`} ` +
    `(${share} % des Zeitraums) ist kein Wert eingegangen; dort ist die Linie unterbrochen ` +
    "und nicht geglättet."
  );
}

/** Alle Beträge, die die Achse tragen muss — beim Mittel auch die Spanne. */
function spanOf(history: History): readonly number[] {
  return history.points.flatMap((point) =>
    history.aggregate === "mean"
      ? [Math.abs(point.v), Math.abs(point.min), Math.abs(point.max)]
      : [Math.abs(point.v)],
  );
}
